import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import { agentLinks, agentLink } from '../../lib/ui-links.js';
import type { Agent, CreateAgentInput, UpdateAgentInput } from '../../types/index.js';
import type { ListOptions, CreateOptions, UpdateOptions, DeleteOptions, ConfirmAnswer } from './index.js';
import { AGENTS_WIP_BANNER_SHORT } from '../../lib/agents-wip-banner.js';

export function registerAgentsCrud(agents: Command): void {
  // List agents
  agents
    .command('list')
    .description('List all agents')
    .option('-l, --limit <n>', 'Limit results', '50')
    .option('-o, --offset <n>', 'Offset for pagination')
    .option('-n, --name <name>', 'Filter by name')
    .option('-s, --status <status>', 'Filter by status (active, paused, archived)')
    .addHelpText('after', `
${AGENTS_WIP_BANNER_SHORT}

Examples:
  ${chalk.dim('$')} mutagent agents list
  ${chalk.dim('$')} mutagent agents list --status active
  ${chalk.dim('$')} mutagent agents list --name "reviewer" --json
    `)
    .action(async (options: ListOptions) => {
      const isJson = getJsonFlag(agents);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const filters: { limit?: number; offset?: number; name?: string; status?: string } = {
          limit: parseInt(options.limit, 10) || 50,
        };

        if (options.offset) {
          filters.offset = parseInt(options.offset, 10);
        }
        if (options.name) {
          filters.name = options.name;
        }
        if (options.status) {
          filters.status = options.status;
        }

        const result = await client.listAgents(filters);

        if (isJson) {
          // Summary view — only IDs and key fields. Use `agents get <id>` for full details.
          const withLinks = result.data.map((a: Agent) => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            model: a.model,
            status: a.status,
            isPublic: a.isPublic,
            updatedAt: a.updatedAt,
            _links: agentLinks(a.id),
          }));
          output.output({ ...result, data: withLinks });
        } else {
          // Format for table
          const formatted = result.data.map((a: Agent) => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            model: a.model,
            status: a.status,
            isPublic: a.isPublic ? 'Yes' : 'No',
            updated: a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : 'N/A',
            url: agentLink(a.id),
          }));
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get agent
  agents
    .command('get')
    .description('Get agent details')
    .argument('<id>', 'Agent ID')
    .addHelpText('after', `
${AGENTS_WIP_BANNER_SHORT}

Examples:
  ${chalk.dim('$')} mutagent agents get <agent-id>
  ${chalk.dim('$')} mutagent agents get <agent-id> --json
    `)
    .action(async (id: string) => {
      const isJson = getJsonFlag(agents);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const agent = await client.getAgent(id);

        if (isJson) {
          output.output({ ...agent, _links: agentLinks(agent.id) });
        } else {
          // Format for display
          const formatted: Record<string, unknown> = {
            id: agent.id,
            name: agent.name,
            slug: agent.slug,
            description: agent.description ?? 'N/A',
            model: agent.model,
            status: agent.status,
            isPublic: agent.isPublic ? 'Yes' : 'No',
            maxTurns: agent.maxTurns ?? 'N/A',
            permissionMode: agent.permissionMode ?? 'default',
            allowedTools: agent.allowedTools?.join(', ') ?? 'All',
            disallowedTools: agent.disallowedTools?.join(', ') ?? 'None',
            tags: agent.tags?.join(', ') ?? 'None',
            createdBy: agent.createdBy ?? 'N/A',
            createdAt: agent.createdAt ? new Date(agent.createdAt).toLocaleString() : 'N/A',
            updatedAt: agent.updatedAt ? new Date(agent.updatedAt).toLocaleString() : 'N/A',
            url: agentLink(agent.id),
          };
          output.output(formatted);

          // Show system prompt separately for readability
          if (agent.systemPrompt) {
            console.log(chalk.bold('\nSystem Prompt:'));
            console.log(chalk.gray('─'.repeat(60)));
            console.log(agent.systemPrompt);
            console.log(chalk.gray('─'.repeat(60)));
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Create agent
  agents
    .command('create')
    .description('Create a new agent')
    .option('-d, --data <json>', 'Agent as JSON string (recommended for CI/scripts/agents)')
    .option('-n, --name <name>', 'Agent name')
    .option('-s, --slug <slug>', 'Agent slug (URL-friendly identifier)')
    .option('-p, --system-prompt <prompt>', 'System prompt')
    .option('-m, --model <model>', 'Model (claude-sonnet-4-5, claude-opus-4-5, claude-haiku-4-5)')
    .option('--description <desc>', 'Agent description')
    .addHelpText('after', `
${AGENTS_WIP_BANNER_SHORT}

Examples:
  ${chalk.dim('$')} mutagent agents create --name "Code Reviewer" --slug code-reviewer --system-prompt "You are a code reviewer..."
  ${chalk.dim('$')} mutagent agents create -d '{"name":"Code Reviewer","slug":"code-reviewer","systemPrompt":"You are a code reviewer..."}'

Expected JSON (--data):
  ${chalk.dim('{"name":"<name>","slug":"<slug>","systemPrompt":"<system prompt>","model":"<model-id>","description":"<description>"}')}

Input Methods (pick one, priority order):
  --name/--slug/...  Individual flags ${chalk.green('(recommended)')}
  -d, --data         Inline JSON object (CI/scripts/agents)

${chalk.red('Required: name, slug, systemPrompt.')} ${chalk.dim('CLI flags override --data fields.')}
    `)
    .action(async (options: CreateOptions) => {
      const isJson = getJsonFlag(agents);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        let data: CreateAgentInput;

        if (options.data) {
          try {
            data = JSON.parse(options.data) as CreateAgentInput;
          } catch {
            throw new MutagentError(
              'INVALID_JSON',
              'Invalid JSON in --data flag',
              "Provide a valid JSON object, e.g., '{\"name\":\"my-agent\",\"slug\":\"my-agent\",\"systemPrompt\":\"You are...\"}'"
            );
          }
          // CLI flags override --data fields
          if (options.name) data.name = options.name;
          if (options.slug) data.slug = options.slug;
          if (options.systemPrompt) data.systemPrompt = options.systemPrompt;
          if (options.model) data.model = options.model;
          if (options.description) data.description = options.description;
        } else if (options.name && options.slug && options.systemPrompt) {
          data = {
            name: options.name,
            slug: options.slug,
            systemPrompt: options.systemPrompt,
          };
          if (options.model) data.model = options.model;
          if (options.description) data.description = options.description;
        } else {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'Either --data or (--name, --slug, and --system-prompt) are required',
            'Use --data for inline JSON or provide --name, --slug, and --system-prompt'
          );
        }

        const client = await getSDKClient();
        const agent = await client.createAgent(data);

        if (!isJson) output.success(`Created agent: ${agent.name} (${agent.slug})`);
        output.output(agent);
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Update agent
  agents
    .command('update')
    .description('Update an agent')
    .argument('<id>', 'Agent ID')
    .option('-d, --data <json>', 'Agent updates as JSON string (CI/scripts/agents)')
    .option('-n, --name <name>', 'New name')
    .option('-p, --system-prompt <prompt>', 'New system prompt')
    .option('-m, --model <model>', 'New model')
    .option('--description <desc>', 'New description')
    .option('-s, --status <status>', 'New status (active, paused, archived)')
    .addHelpText('after', `
${AGENTS_WIP_BANNER_SHORT}

Examples:
  ${chalk.dim('$')} mutagent agents update <id> --name "New Name"
  ${chalk.dim('$')} mutagent agents update <id> --system-prompt "Updated prompt" --status active
  ${chalk.dim('$')} mutagent agents update <id> -d '{"name":"New Name","systemPrompt":"Updated prompt"}'

Input Methods (pick one, priority order):
  --name/--system-prompt/...  Individual flags ${chalk.green('(recommended)')}
  -d, --data                  Inline JSON object (CI/scripts/agents)

${chalk.dim('CLI flags override --data fields.')}
    `)
    .action(async (id: string, options: UpdateOptions) => {
      const isJson = getJsonFlag(agents);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        let data: UpdateAgentInput = {};

        if (options.data) {
          try {
            data = JSON.parse(options.data) as UpdateAgentInput;
          } catch {
            throw new MutagentError(
              'INVALID_JSON',
              'Invalid JSON in --data flag',
              "Provide a valid JSON object, e.g., '{\"name\":\"new-name\",\"systemPrompt\":\"Updated prompt\"}'"
            );
          }
          // CLI flags override --data fields
          if (options.name) data.name = options.name;
          if (options.systemPrompt) data.systemPrompt = options.systemPrompt;
          if (options.model) data.model = options.model;
          if (options.description) data.description = options.description;
          if (options.status) data.status = options.status;
        } else {
          if (options.name) data.name = options.name;
          if (options.systemPrompt) data.systemPrompt = options.systemPrompt;
          if (options.model) data.model = options.model;
          if (options.description) data.description = options.description;
          if (options.status) data.status = options.status;
        }

        if (Object.keys(data).length === 0) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'No update data provided',
            'Use --data, --name, --system-prompt, --model, --description, or --status'
          );
        }

        const client = await getSDKClient();
        const agent = await client.updateAgent(id, data);

        if (!isJson) output.success(`Updated agent: ${agent.name}`);
        output.output(agent);
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Delete agent
  agents
    .command('delete')
    .description('Delete an agent')
    .argument('<id>', 'Agent ID')
    .option('--force', 'Skip confirmation')
    .addHelpText('after', `
${AGENTS_WIP_BANNER_SHORT}

Examples:
  ${chalk.dim('$')} mutagent agents delete <id>
  ${chalk.dim('$')} mutagent agents delete <id> --force
  ${chalk.dim('$')} mutagent agents delete <id> --force --json

${chalk.dim('Tip: Use --force to skip confirmation (required for non-interactive/CI usage).')}
    `)
    .action(async (id: string, options: DeleteOptions) => {
      const isJson = getJsonFlag(agents);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        if (!options.force && !isJson) {
          const inquirer = (await import('inquirer')).default;
          const answers = await inquirer.prompt<ConfirmAnswer>([{
            type: 'confirm',
            name: 'confirm',
            message: `Delete agent ${id}? This action cannot be undone.`,
            default: false,
          }]);

          if (!answers.confirm) {
            output.info('Cancelled');
            return;
          }
        }

        const client = await getSDKClient();
        await client.deleteAgent(id);

        output.success(`Deleted agent: ${id}`);
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
